<template>
  <LeadHistoryCard
    v-bind="attributes"
    :pick-up-call-target-id="pickUpCallTargetId"
    @update:pick-up-call-target-id="$emit('update:pickUpCallTargetId', $event)"
  >
    <template #date>
      <div>{{ historiedAt }}</div>
    </template>
    <BEditableContentTextarea
      v-model="content"
      class="freeaction-content"
      :placeholder="$t('freeAction.content')"
      :editable="editable"
      @update:model-value="onContentSaved"
      @click="onEditStart"
      @cancel="onEditCancel"
    />
    <p
      v-if="showSfSyncNotice && isEditing && sfConnectedStatus !== SalesforceConst.SyncedFailed"
      class="sf-sync-notice"
    >
      {{ $t('todoSync.sfSyncNotice') }}
    </p>
    <BLayout
      justify-end
      class="mt-50"
    >
      <BCopyButton
        :text="content"
        :title="$t('freeAction.content')"
      />
    </BLayout>
    <template
      v-if="editable"
      #action
    >
      <BBtn
        v-if="isAvailableSalesforceApiFeature"
        class="mt-2 mr-2"
        size="small"
        fab
        flat
      >
        <BSfStatusIcon
          :connect-status="sfConnectedStatus"
          @open-sf-modal="openSfModal(sfFreeActionTaskName)"
        />
      </BBtn>
      <SfStatusModal
        v-if="isAvailableSalesforceApiFeature"
        :modal-name="sfFreeActionTaskName"
        :sf-related-link-info="freeAction.sfRelatedLinkInfo"
        :sf-exec-sync-btn="false"
        :resync-task-info="resyncTaskInfo"
        @synced="updateSynced"
      />
      <BBtn
        size="small"
        fab
        flat
        data-test="delete"
        @click.stop="$emit('click-delete', freeAction.id)"
      >
        <BIcon size="small">
          delete_forever
        </BIcon>
      </BBtn>
    </template>
  </LeadHistoryCard>
</template>

<script>
import { mapWaitingActions } from 'vue-wait';
import { mapGetters } from 'vuex';
import SfStatusModal from '@/components/organisms/user/general/modal/salesforce/SfStatusModal.vue';
import LeadHistoryCard from '@/components/organisms/user/lead/history/LeadHistoryCard.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useErrorHandler } from '@/composable/error-handler';
import { useSfTodoSyncNotice } from '@/composable/user/salesforce/use-sf-todo-sync-notice';
import SalesforceConst from '@/const/salesforce';
import BSfStatusIcon from '@/plugins/biscuet-materials/components/molecules/BSfStatusIcon.vue';
import { formatShorterDateTime } from '@/utils/date-time';

export default {
  components: {
    LeadHistoryCard,
    SfStatusModal,
    BSfStatusIcon,
  },
  inheritAttrs: false,
  props: {
    callTargetId: {
      type: Number,
      required: true,
    },
    callTarget: {
      type: Object,
      required: false,
      default: undefined,
    },
    freeAction: {
      type: Object,
      required: true,
    },
    expand: Boolean,
    editable: {
      type: Boolean,
      default: true,
    },
    pickUpCallTargetId: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  emits: [
    'click-delete',
    'update:pickUpCallTargetId',
  ],
  setup() {
    const { isAvailableSalesforceApiFeature } = useAvailableFeatureCheck();
    const { defaultHandler } = useErrorHandler();
    const { showSfSyncNotice } = useSfTodoSyncNotice('free_action');
    return {
      isAvailableSalesforceApiFeature,
      defaultHandler,
      showSfSyncNotice,
      SalesforceConst,
    };
  },
  data () {
    return {
      content: this.freeAction.content,
      isEditing: false,
      sfConnectedStatusValue: this.freeAction.sfRelatedLinkInfo?.sfRelatedLinks[0]?.status,
    };
  },
  computed: {
    ...mapGetters('user', [
      'callTarget',
    ]),
    attributes () {
      return {
        'text': this.$t('freeAction.title'),
        'user': this.freeAction.user,
        'icon': 'add',
        'icon-type': 'default',
        'toggle': true,
        'expand': this.expand,
        'call-target-id': this.callTargetId,
        'history': this.freeAction,
        'call-target': this.callTarget,
      };
    },
    historiedAt () {
      return formatShorterDateTime(this.freeAction.historiedAt);
    },
    sfConnectedStatus() {
      return this.sfConnectedStatusValue;
    },
    sfFreeActionTaskName() {
      return `sfTaskStatusFreeActionModal${this.freeAction.id}`;
    },
    resyncTaskInfo() {
      return {
        id: this.freeAction.id,
        callTargetId: this.callTargetId,
        todoType: this.freeAction.type,
      };
    },
  },
  watch: {
    'freeAction.content' (newVal) {
      this.content = newVal;
    },
  },
  methods: {
    ...mapWaitingActions('user', {
      updateFreeActionAction: 'updateFreeActionAction',
      getCallTargetsBackgroundAction: { action: 'getCallTargetsAction', loader: 'getCallTargetsBackgroundWait' },
      getLeadHistoriesBackgroundAction: { action: 'getLeadHistoriesAction', loader: 'getLeadHistoriesBackgroundWait' },
    }),
    ...mapWaitingActions('userUi', {
      setTargetHeaderMoveButtonDisabledAction: 'setTargetHeaderMoveButtonDisabledWait',
    }),
    onEditStart () {
      this.isEditing = true;
      this.setTargetHeaderMoveButtonDisable(true);
    },
    onEditCancel () {
      this.isEditing = false;
      this.setTargetHeaderMoveButtonDisable(false);
    },
    onContentSaved () {
      this.isEditing = false;
      this.updateFreeAction();
    },
    setTargetHeaderMoveButtonDisable (status) {
      this.setTargetHeaderMoveButtonDisabledAction(status);
    },
    updateFreeAction () {
      this.setTargetHeaderMoveButtonDisabledAction(false);
      this.updateFreeActionAction({
        freeActionId: this.freeAction.id,
        callTargetId: this.callTargetId,
        body: { freeAction: { ...this.freeAction, content: this.content } },
        errorHandlers: {
          422: this.defaultHandler,
        },
      }).then(() => {
        return this.getLeadHistoriesBackgroundAction({ callTargetId: this.callTargetId });
      }).then(() => {
        return this.getCallTargetsBackgroundAction({ callListId: this.$route.params.id });
      });
    },
    openSfModal(modalName) {
      this.$modal.show(modalName);
    },
    updateSynced() {
      this.sfConnectedStatusValue = SalesforceConst.Synced;
    },
  },
};
</script>

<style lang="scss" scoped>
  .freeaction-content {
    margin-left: -$basespace-100;
    margin-right: -$basespace-100;
  }
  .sf-sync-notice {
    font-size: $fontsize-100;
    color: $mars;
    margin: $basespace-100 0 $basespace-200;
  }
</style>
